<template>
    <td 
        class="date-cell" 
        :class="{ 'editing': isEditing }"
        @click="enterEditMode"
        @keydown.enter.prevent="handleEnter"
        ref="cellContainer"
    >
        <!-- VIEW MODE -->
        <div v-if="!isEditing || !editable" class="view-mode" 
            :class="{
                'cell-invalid': invalid
            }"
        >
            <span>{{ Value || placeholder }}</span>
            <i class="far fa-calendar-alt calendar-icon"></i>
        </div>

        <!-- EDIT MODE (Manual Text Input with Mask) -->
        <input
            v-else
            :class="{
                'cell-invalid': invalid
            }"
            ref="textInput"
            type="text"
            class="form-control-clean"
            :value="Value"
            :placeholder="format"
            @input="onInput"
            @keydown="onKeyDown"
            @paste.prevent="onPaste" 
            @blur="onBlur"
        />
    </td>
</template>

<script>
import { EventBus } from '@/event-bus.js';

export default {
    name: 'EditableTimeCell',

    props: {
        name: { type: String, required: true },
        value: String, // Expected ISO time: HH:mm:ss
        editable: { type: Boolean, default: true },

        // '24h' or '12h'
        timeFormat: {
            type: String,
            default: '24h',
            validator: v => ['24h', '12h'].includes(v)
        },

        // 'mm' or 'mm:ss'
        precision: {
            type: String,
            default: 'mm',
            validator: v => ['mm', 'mm:ss'].includes(v)
        }
    },

    data() {
        return {
            isEditing: false,
            invalid: false,

            // Digits only: HHmmss
            rawDigits: '',

            // AM / PM (12h only)
            period: '',

            // Visual value
            Value: ''
        };
    },

    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (!newValue) {
                    this.rawDigits = '';
                    this.period = '';
                    this.Value = '';
                    return;
                }

                // Expect HH:mm:ss
                const [h, m, s = '00'] = newValue.split(':');

                let hours = parseInt(h, 10);

                if (this.timeFormat === '12h') {
                    this.period = hours >= 12 ? 'PM' : 'AM';
                    hours = hours % 12 || 12;
                }

                this.rawDigits = `${hours
                    .toString()
                    .padStart(2, '0')}${m}${s}`;

                this.Value = this.applyTimeMask();
            }
        }
    },

    methods: {
        enterEditMode() {
            this.isEditing = true;
            this.$nextTick(() => {
                this.$refs.textInput.focus();
            });
        },

        handleEnter() {
            if (this.isEditing) {
                this.isEditing = false;
            }
            this.$emit('next-cell');
        },

        applyTimeMask() {
            let result = '';

            if (this.rawDigits.length > 0) {
                result += this.rawDigits.substring(0, 2);

                if (this.rawDigits.length > 2) {
                    result += ':' + this.rawDigits.substring(2, 4);

                    if (
                        this.precision === 'mm:ss' &&
                        this.rawDigits.length > 4
                    ) {
                        result += ':' + this.rawDigits.substring(4, 6);
                    }
                }
            }

            if (this.timeFormat === '12h' && result.length >= 5) {
                result += ' ' + (this.period || 'AM');
            }

            return result;
        },

        onBlur() {
            this.isEditing = false;
            this.emitIfValid();
        },

        onKeyDown(e) {
            const input = e.target;
            const cursorPos = input.selectionStart;

            const allowedKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Delete'
            ];

            if (allowedKeys.includes(e.key)) {
                return;
            }

            // Toggle AM/PM with A or P
            if (this.timeFormat === '12h') {
                if (e.key.toUpperCase() === 'A') {
                    this.period = 'AM';
                    e.preventDefault();
                    return;
                }

                if (e.key.toUpperCase() === 'P') {
                    this.period = 'PM';
                    e.preventDefault();
                    return;
                }
            }

            // Allow colon only at valid positions
            if (e.key === ':') {
                if (cursorPos !== 2 && cursorPos !== 5) {
                    e.preventDefault();
                }
                return;
            }

            // Allow digits only
            if (!/^\d$/.test(e.key)) {
                e.preventDefault();
            }
        },

        onInput(e) {
            const input = e.target;
            const cursorPos = input.selectionStart;

            let digits = input.value.replace(/\D/g, '');

            // HHmm or HHmmss
            digits = digits.substring(
                0,
                this.precision === 'mm:ss' ? 6 : 4
            );

            this.rawDigits = digits;
            this.Value = this.applyTimeMask();

            input.value = this.Value;

            this.$nextTick(() => {
                const newCursorPos = Math.min(
                    cursorPos,
                    this.Value.length
                );

                input.setSelectionRange(newCursorPos, newCursorPos);
            });

            this.emitIfValid();
        },

        emitIfValid() {
            const expectedLength =
                this.precision === 'mm:ss' ? 6 : 4;

            if (this.rawDigits.length !== expectedLength) {
                this.emitChanged('');
                return;
            }

            let hours = parseInt(this.rawDigits.substring(0, 2), 10);
            const minutes = this.rawDigits.substring(2, 4);
            const seconds =
                this.precision === 'mm:ss'
                ? this.rawDigits.substring(4, 6)
                : '00';

            if (this.timeFormat === '12h') {
                if (this.period === 'PM' && hours < 12) {
                    hours += 12;
                }
                
                if (this.period === 'AM' && hours === 12) {
                    hours = 0;
                }
            }

            if (hours > 23 || minutes > 59 || seconds > 59) {
                this.emitChanged('');
                return;
            }

            const isoTime = `${hours
                .toString()
                .padStart(2, '0')}:${minutes}:${seconds}`;

            this.$emit('input', isoTime);
            this.emitChanged(isoTime);
        },

        onPaste(e) {
            e.preventDefault();

            const pasted = (
                e.clipboardData || window.clipboardData
            ).getData('text');

            const digits = pasted.replace(/\D/g, '');

            if (!digits) {
                return;
            }

            this.rawDigits = digits.substring(
                0,
                this.precision === 'mm:ss' ? 6 : 4
            );

            if (this.timeFormat === '12h') {
                if (/pm/i.test(pasted)) {
                    this.period = 'PM';
                }

                if (/am/i.test(pasted)) {
                    this.period = 'AM';
                }
            }

            this.Value = this.applyTimeMask();
        },

        invalidateState(state) {
            this.$nextTick(() => this.invalid = state);
        },

        emitChanged(value) {
            const changed = {
                "Changed":true,
                "ComponentName":this.name,
                "Value" : value
            } 
            EventBus.$emit('onChanged', changed);
        }
    }
}
</script>

<style scoped>
.date-cell {
    cursor: pointer;
    min-width: 140px;
    vertical-align: middle !important;
    position: relative;
    padding: 0 !important;
}

.view-mode {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
}

.calendar-icon {
    color: #007bff;
    opacity: 0.7;
}

.form-control-clean {
    width: 100%;
    height: 38px;
    border: 2px solid #007bff;
    padding: 0 8px;
    outline: none;
    font-family: monospace;
    background: white;
}

.date-cell:focus-within {
    box-shadow: inset 0 0 0 2px #007bff;
}

.cell-invalid { 
    border: 1px solid red; 
}
</style>